import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import {
  Plus, Trash2, Save, ArrowUp, ArrowDown, X, Search,
  Package, Users, Star, GripVertical, Edit3, ImageOff,
} from 'lucide-react';

const EMPTY_TIER = {
  id: '',
  name: '',
  subtitle: '',
  price: 0,
  currency: 'NZD',
  seats_total: 0,
  seats_taken: 0,
  highlight: false,
  inclusions: [],
  linked_products: [],
  order_index: 0,
};

function slugifyId(str) {
  return (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/* ------------------------------------------------------------------ */
/*  Product picker                                                     */
/* ------------------------------------------------------------------ */
function ProductPicker({ open, onClose, onAdd, alreadySelected }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get('/admin/catalog-slim');
      setItems(data.items || []);
    })();
  }, [open]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter(i =>
      (i.name || '').toLowerCase().includes(t) ||
      (i.slug || '').toLowerCase().includes(t) ||
      (i.category || '').toLowerCase().includes(t),
    );
  }, [q, items]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(75,24,56,0.35)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="tier-product-picker"
    >
      <div
        className="h-panel w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
          <div>
            <div className="h-eyebrow">Add product from inventory</div>
            <div className="h-serif text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Product library</div>
          </div>
          <button type="button" className="h-btn-outline text-xs" onClick={onClose} data-testid="tier-picker-close">
            <X size={14} className="mr-1.5" />Close
          </button>
        </div>

        <div className="px-5 pt-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" style={{ color: 'var(--heritage-plum)' }} />
            <input
              className="h-input pl-10"
              placeholder="Search by name, slug or category..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="tier-picker-search"
              autoFocus
            />
          </div>
        </div>

        <div className="p-5 overflow-y-auto grid sm:grid-cols-2 gap-3">
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-8 opacity-70" style={{ color: 'var(--heritage-plum)' }}>
              No products found. Add products in the Products tab first.
            </div>
          )}
          {filtered.map((p) => {
            const alreadyAdded = alreadySelected.some(x => x.product_slug === p.slug);
            const img = p.image_base64 || p.image_url || '';
            return (
              <button
                type="button"
                key={p.slug}
                disabled={alreadyAdded}
                onClick={() => { onAdd(p); onClose(); }}
                className={`text-left rounded-xl border p-3 flex gap-3 items-center transition ${alreadyAdded ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-sm'}`}
                style={{ borderColor: 'rgba(75,24,56,0.25)', background: '#FBF8F1' }}
                data-testid={`tier-picker-item-${p.slug}`}
              >
                <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: '#F0E9DA' }}>
                  {img ? (
                    <img src={img} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <ImageOff size={18} style={{ color: 'var(--heritage-plum)' }} className="opacity-50" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="h-serif text-base leading-tight truncate" style={{ color: 'var(--heritage-plum)' }}>{p.name}</div>
                  <div className="text-xs opacity-70 truncate" style={{ color: 'var(--heritage-plum)' }}>
                    {p.category || 'Uncategorised'} · {p.slug}
                  </div>
                  {alreadyAdded && <div className="text-[11px] mt-1" style={{ color: '#9A4B7E' }}>Already added</div>}
                </div>
                <Plus size={16} style={{ color: 'var(--heritage-plum)' }} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Linked-products editor (inside tier form)                          */
/* ------------------------------------------------------------------ */
function LinkedProductsEditor({ value, onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const list = value || [];

  const add = (p) => {
    onChange([...list, {
      product_slug: p.slug,
      quantity: 1,
      note: '',
      product: p, // client-side hydration for preview
    }]);
  };

  const update = (idx, patch) => {
    const next = list.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const remove = (idx) => {
    const next = list.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const move = (idx, dir) => {
    const next = list.slice();
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-eyebrow">Bundle contents</div>
          <div className="text-xs opacity-70 mt-0.5" style={{ color: 'var(--heritage-plum)' }}>
            Products linked here appear on each founder&#39;s <span className="italic">My Founder Bundle</span> page.
          </div>
        </div>
        <button type="button" className="h-btn-outline text-xs" onClick={() => setPickerOpen(true)} data-testid="tier-add-product-btn">
          <Plus size={14} className="mr-1.5" /> Add product
        </button>
      </div>

      {list.length === 0 && (
        <div className="rounded-xl border border-dashed p-6 text-center text-sm opacity-70"
             style={{ borderColor: 'rgba(75,24,56,0.35)', color: 'var(--heritage-plum)' }}>
          No products linked yet. Add items from the product inventory to build this tier&#39;s bundle.
        </div>
      )}

      <div className="space-y-2">
        {list.map((lp, idx) => {
          const p = lp.product || {};
          const img = p.image_base64 || p.image_url || '';
          const missing = !lp.product;
          return (
            <div key={`${lp.product_slug}-${idx}`}
                 className="rounded-xl border p-3 flex flex-col md:flex-row md:items-center gap-3"
                 style={{ borderColor: 'rgba(75,24,56,0.25)', background: '#FBF8F1' }}
                 data-testid={`tier-linked-${lp.product_slug}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex flex-col">
                  <button type="button" className="p-1 opacity-70 hover:opacity-100" onClick={() => move(idx, -1)} aria-label="Move up">
                    <ArrowUp size={14} style={{ color: 'var(--heritage-plum)' }} />
                  </button>
                  <button type="button" className="p-1 opacity-70 hover:opacity-100" onClick={() => move(idx, +1)} aria-label="Move down">
                    <ArrowDown size={14} style={{ color: 'var(--heritage-plum)' }} />
                  </button>
                </div>
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: '#F0E9DA' }}>
                  {img ? <img src={img} alt={p.name} className="w-full h-full object-cover" /> : <ImageOff size={16} className="opacity-50" style={{ color: 'var(--heritage-plum)' }} />}
                </div>
                <div className="min-w-0">
                  <div className="h-serif text-base leading-tight truncate" style={{ color: 'var(--heritage-plum)' }}>
                    {p.name || lp.product_slug}
                  </div>
                  <div className="text-xs opacity-70 truncate" style={{ color: 'var(--heritage-plum)' }}>
                    {missing ? 'Product not found or archived' : (p.category || 'Uncategorised')} · {lp.product_slug}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 md:w-auto w-full">
                <div className="flex flex-col">
                  <label className="h-label text-[10px]">Qty</label>
                  <input
                    className="h-input w-20"
                    type="number" min="1" step="1"
                    value={lp.quantity}
                    onChange={(e) => update(idx, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                    data-testid={`tier-linked-qty-${lp.product_slug}`}
                  />
                </div>
                <div className="flex-1 flex flex-col">
                  <label className="h-label text-[10px]">Note (e.g. colour, size)</label>
                  <input
                    className="h-input"
                    value={lp.note || ''}
                    placeholder="in Pinkle colourway"
                    onChange={(e) => update(idx, { note: e.target.value })}
                    data-testid={`tier-linked-note-${lp.product_slug}`}
                  />
                </div>
                <button type="button" className="h-btn-outline text-xs mt-4" onClick={() => remove(idx)} aria-label="Remove"
                        data-testid={`tier-linked-remove-${lp.product_slug}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <ProductPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdd={add}
        alreadySelected={list}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tier form                                                          */
/* ------------------------------------------------------------------ */
function TierForm({ initial, onSave, onDelete, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_TIER);
  const [inclusionsText, setInclusionsText] = useState((initial?.inclusions || []).join('\n'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isNew = !initial?.id;

  useEffect(() => {
    setForm(initial || EMPTY_TIER);
    setInclusionsText((initial?.inclusions || []).join('\n'));
    setError('');
  }, [initial]);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const inclusions = inclusionsText.split('\n').map(s => s.trim()).filter(Boolean);
      const linked_products = (form.linked_products || []).map(lp => ({
        product_slug: lp.product_slug,
        quantity: parseInt(lp.quantity) || 1,
        note: lp.note || '',
      }));
      const payload = {
        ...form,
        id: form.id || slugifyId(form.name) || undefined,
        inclusions,
        linked_products,
        price: parseFloat(form.price) || 0,
        seats_total: parseInt(form.seats_total) || 0,
        seats_taken: parseInt(form.seats_taken) || 0,
        order_index: parseInt(form.order_index) || 0,
      };
      await onSave(payload, isNew);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Panel className="p-6">
      <form onSubmit={submit} className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-eyebrow">{isNew ? 'New tier' : 'Editing tier'}</div>
            <div className="h-serif text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>
              {form.name || 'Untitled tier'}
            </div>
          </div>
          {onCancel && (
            <button type="button" className="h-btn-outline text-xs" onClick={onCancel} data-testid="tier-form-close">
              <X size={14} className="mr-1.5" />Close
            </button>
          )}
        </div>

        {/* Details */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="h-label">Tier ID</label>
            <input
              className="h-input"
              disabled={!isNew}
              placeholder="auto-generated from name"
              value={form.id}
              onChange={(e) => setForm({ ...form, id: slugifyId(e.target.value) })}
              data-testid="tier-id"
            />
            <div className="text-[11px] opacity-60 mt-1" style={{ color: 'var(--heritage-plum)' }}>
              Permanent, used to reference this tier. Locked after creation.
            </div>
          </div>
          <div>
            <label className="h-label">Display order</label>
            <input className="h-input" type="number" value={form.order_index}
              onChange={(e) => setForm({ ...form, order_index: e.target.value })}
              data-testid="tier-order" />
          </div>
          <div className="md:col-span-2">
            <label className="h-label">Name</label>
            <input className="h-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="tier-name" />
          </div>
          <div className="md:col-span-2">
            <label className="h-label">Subtitle</label>
            <input className="h-input" value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              data-testid="tier-subtitle" />
          </div>
          <div>
            <label className="h-label">Price</label>
            <input className="h-input" type="number" step="0.01" min="0" value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              data-testid="tier-price" />
          </div>
          <div>
            <label className="h-label">Currency</label>
            <input className="h-input" value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })} />
          </div>
          <div>
            <label className="h-label">Seats total</label>
            <input className="h-input" type="number" min="0" value={form.seats_total}
              onChange={(e) => setForm({ ...form, seats_total: e.target.value })}
              data-testid="tier-seats-total" />
          </div>
          <div>
            <label className="h-label">Seats taken</label>
            <input className="h-input" type="number" min="0" value={form.seats_taken}
              onChange={(e) => setForm({ ...form, seats_taken: e.target.value })}
              data-testid="tier-seats-taken" />
          </div>
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <input type="checkbox" checked={!!form.highlight}
                onChange={(e) => setForm({ ...form, highlight: e.target.checked })}
                data-testid="tier-highlight" />
              <Star size={14} /> Mark as featured tier (highlighted on public site)
            </label>
          </div>
        </div>

        {/* Inclusions */}
        <div>
          <label className="h-label">Inclusions (one per line, shown as bullet points)</label>
          <textarea className="h-input" rows={6} value={inclusionsText}
            onChange={(e) => setInclusionsText(e.target.value)}
            data-testid="tier-inclusions" />
        </div>

        {/* Linked products */}
        <LinkedProductsEditor
          value={form.linked_products || []}
          onChange={(next) => setForm({ ...form, linked_products: next })}
        />

        {error && <div className="text-sm" style={{ color: '#9A2C2C' }}>{error}</div>}

        <div className="flex flex-wrap gap-2 justify-end">
          {onDelete && !isNew && (
            <button type="button" className="h-btn-outline text-xs" onClick={() => onDelete(initial.id)}
                    data-testid="tier-delete">
              <Trash2 size={14} className="mr-1.5" /> Delete
            </button>
          )}
          <button type="submit" className="h-btn" disabled={saving} data-testid="tier-save">
            <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save tier'}
          </button>
        </div>
      </form>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */
export default function AdminTiers() {
  const [tiers, setTiers] = useState([]);
  const [editing, setEditing] = useState(null); // tier or 'new'
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/tiers');
      // Normalise linked_products: unwrap so form works with slug/qty/note + product for preview
      const list = (data.tiers || []).map(t => ({
        ...t,
        linked_products: (t.linked_products || []).map(lp => ({
          product_slug: lp.product_slug,
          quantity: lp.quantity || 1,
          note: lp.note || '',
          product: lp.product || null,
        })),
      }));
      setTiers(list);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2200);
  };

  const save = async (tier, isNew) => {
    // strip client-only "product" preview before sending
    const payload = {
      ...tier,
      linked_products: (tier.linked_products || []).map(lp => ({
        product_slug: lp.product_slug,
        quantity: lp.quantity,
        note: lp.note,
      })),
    };
    if (isNew) {
      await api.post('/admin/tiers', payload);
      showToast('Tier created');
    } else {
      await api.put(`/admin/tiers/${tier.id}`, payload);
      showToast('Tier saved');
    }
    setEditing(null);
    load();
  };

  const del = async (tid) => {
    if (!window.confirm('Delete this tier? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/tiers/${tid}`);
      showToast('Tier deleted');
      setEditing(null);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const move = async (id, dir) => {
    const idx = tiers.findIndex(t => t.id === id);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= tiers.length) return;
    const next = tiers.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setTiers(next);
    await api.post('/admin/tiers/reorder', { ids: next.map(t => t.id) });
    load();
  };

  return (
    <div className="space-y-6">
      <Panel className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="h-eyebrow">Founders Club</div>
          <div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Tier Management</div>
          <div className="text-sm opacity-80 mt-2 max-w-2xl" style={{ color: 'var(--heritage-plum)' }}>
            Configure the tiers a member can join, edit written inclusions, and attach live products from the inventory. Linked products appear inside each founder&#39;s bundle page.
          </div>
        </div>
        <button className="h-btn" onClick={() => setEditing('new')} data-testid="tier-new">
          <Plus size={14} className="mr-1.5" /> New tier
        </button>
      </Panel>

      {toast && (
        <div className="fixed bottom-6 right-6 z-40 h-panel-thin px-4 py-2 shadow-lg"
             style={{ background: 'var(--heritage-plum)', color: 'var(--heritage-cream)' }}
             data-testid="tier-toast">
          {toast}
        </div>
      )}

      {editing === 'new' && (
        <TierForm initial={null} onSave={save} onCancel={() => setEditing(null)} />
      )}
      {editing && editing !== 'new' && (
        <TierForm initial={editing} onSave={save} onDelete={del} onCancel={() => setEditing(null)} />
      )}

      <div className="grid md:grid-cols-2 gap-5">
        {loading && (
          <Panel className="p-8 md:col-span-2 text-center opacity-70" style={{ color: 'var(--heritage-plum)' }}>
            Loading tiers...
          </Panel>
        )}
        {!loading && tiers.length === 0 && (
          <Panel className="p-8 md:col-span-2 text-center" style={{ color: 'var(--heritage-plum)' }}>
            <div className="h-serif text-xl">No tiers yet</div>
            <div className="text-sm opacity-70 mt-2">Create your first Founders Club tier to get started.</div>
          </Panel>
        )}
        {!loading && tiers.map((t, idx) => (
          <Panel key={t.id} className="p-5 flex flex-col gap-3 relative"
                 style={t.highlight ? { borderColor: 'var(--heritage-plum)', boxShadow: '0 0 0 2px rgba(75,24,56,0.15) inset' } : undefined}
                 data-testid={`tier-card-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="h-eyebrow">{t.id}</div>
                  {t.highlight && (
                    <span className="text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(75,24,56,0.1)', color: 'var(--heritage-plum)' }}>
                      <Star size={10} /> Featured
                    </span>
                  )}
                </div>
                <div className="h-serif text-2xl leading-tight mt-1" style={{ color: 'var(--heritage-plum)' }}>{t.name}</div>
                {t.subtitle && (
                  <div className="text-sm opacity-80 italic" style={{ color: 'var(--heritage-plum)' }}>{t.subtitle}</div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>
                  {t.currency} ${Number(t.price || 0).toLocaleString()}
                </div>
                <div className="flex items-center gap-1 text-[11px] opacity-70" style={{ color: 'var(--heritage-plum)' }}>
                  <Users size={11} /> {t.members_count || 0} member{(t.members_count || 0) === 1 ? '' : 's'}
                </div>
                <div className="text-[11px] opacity-70" style={{ color: 'var(--heritage-plum)' }}>
                  {t.seats_taken || 0} / {t.seats_total || 0} seats
                </div>
              </div>
            </div>

            <div className="rounded-lg p-3" style={{ background: '#FBF8F1' }}>
              <div className="h-eyebrow text-[10px] mb-1.5">Inclusions ({(t.inclusions || []).length})</div>
              {(t.inclusions || []).length === 0 ? (
                <div className="text-xs opacity-60" style={{ color: 'var(--heritage-plum)' }}>No inclusions yet</div>
              ) : (
                <ul className="text-xs space-y-1" style={{ color: 'var(--heritage-plum)' }}>
                  {(t.inclusions || []).slice(0, 4).map((i) => (
                    <li key={i} className="flex gap-2"><span>◈</span><span className="opacity-90">{i}</span></li>
                  ))}
                  {(t.inclusions || []).length > 4 && (
                    <li className="opacity-60 italic">+ {(t.inclusions || []).length - 4} more...</li>
                  )}
                </ul>
              )}
            </div>

            <div className="rounded-lg p-3" style={{ background: '#FBF8F1' }}>
              <div className="h-eyebrow text-[10px] mb-1.5 flex items-center gap-1.5">
                <Package size={11} /> Linked products ({(t.linked_products || []).length})
              </div>
              {(t.linked_products || []).length === 0 ? (
                <div className="text-xs opacity-60" style={{ color: 'var(--heritage-plum)' }}>None linked yet</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(t.linked_products || []).map((lp) => {
                    const img = lp.product?.image_base64 || lp.product?.image_url;
                    return (
                      <div key={lp.product_slug || `${t.id}-${lp.id || lp.name}`} className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1"
                           style={{ border: '1px solid rgba(75,24,56,0.25)' }}>
                        <div className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0" style={{ background: '#F0E9DA' }}>
                          {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <Package size={10} style={{ color: 'var(--heritage-plum)' }} />}
                        </div>
                        <span className="text-[11px]" style={{ color: 'var(--heritage-plum)' }}>
                          {lp.quantity}× {lp.product?.name || lp.product_slug}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="flex gap-1">
                <button className="h-btn-outline text-xs" onClick={() => move(t.id, -1)} disabled={idx === 0} aria-label="Move up"
                        data-testid={`tier-move-up-${t.id}`}>
                  <ArrowUp size={14} />
                </button>
                <button className="h-btn-outline text-xs" onClick={() => move(t.id, +1)} disabled={idx === tiers.length - 1} aria-label="Move down"
                        data-testid={`tier-move-down-${t.id}`}>
                  <ArrowDown size={14} />
                </button>
              </div>
              <button className="h-btn text-xs" onClick={() => setEditing(t)} data-testid={`tier-edit-${t.id}`}>
                <Edit3 size={14} className="mr-1.5" /> Edit tier
              </button>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
